/** English messages. The source of truth for keys; `bn` must match exactly. */
export const en = {
  "common.appName": "BrandEx",
  "common.loading": "Loading…",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.retry": "Try again",
  "common.signOut": "Sign out",
  "staffAccount.title": "This is a BrandEx staff account",
  "staffAccount.body": "Staff work in the BrandEx admin panel, where you see the clients you look after. Sign in there with the same email, password and authenticator app.",
  "staffAccount.open": "Open the admin panel",
  "common.refresh": "Refresh",
  "common.error": "Something went wrong. Please try again.",
  "common.language": "Language",

  "auth.signIn": "Sign in",
  "auth.signUp": "Create free account",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.passwordHint": "At least 12 characters. A short sentence works well.",
  "auth.displayName": "Your name",
  "auth.organizationName": "Practice or clinic name (optional)",
  "auth.totpCode": "Authenticator code",
  "auth.forgotPassword": "Forgot password?",
  "auth.noAccount": "New to BrandEx?",
  "auth.haveAccount": "Already have an account?",
  "auth.freeForever": "Free software. No subscription, no card.",
  "auth.resetTitle": "Reset your password",
  "auth.resetSent": "If an account exists for that email, a reset link is on its way.",
  "auth.sendResetLink": "Send reset link",
  "auth.newPassword": "New password",
  "auth.setPassword": "Set new password",
  "auth.passwordUpdated": "Password updated. Please sign in.",
  "auth.verifying": "Verifying your email…",
  "auth.verified": "Email verified. You're all set.",
  "auth.verifyFailed": "This link is invalid or has expired.",
  "auth.registered": "Account created. Check your inbox to verify your email.",

  "error.unauthenticated": "Email or password is incorrect.",
  "error.mfa_required": "Enter the code from your authenticator app.",
  "error.rate_limited": "Too many attempts. Please wait {seconds} seconds.",
  "error.email_taken": "An account with this email already exists.",
  "error.validation_failed": "Please check the highlighted field.",
  "error.network": "Can't reach BrandEx. Check your connection.",
  "error.article_pins_full": "Three articles are already pinned. Unpin one first.",
  "error.social_pins_full": "Three posts are already pinned on this platform. Unpin one first.",

  "nav.overview": "Overview",
  "nav.profile": "Profile",
  "nav.inbox": "Inbox",
  "nav.services": "Services",
  "nav.team": "Team",
  "nav.security": "Security",
  "nav.month": "Your month",
  "nav.research": "Research",
  "nav.schedule": "Schedule",
  "nav.studio": "Studio",
  "nav.approvals": "Approvals",
  "nav.ads": "Ads",
  "nav.google": "Google",
  "nav.bookings": "Bookings",
  "nav.report": "Report",
  "nav.ethics": "Ethics",
  "nav.settings": "Settings",
  "nav.package": "Package and billing",
  "package.title": "Package and billing",
  "package.current": "Current package",
  "package.perMonth": "/ month",
  "package.toQuote": "Price to be quoted by BrandEx",
  "package.included": "Included",
  "package.status.requested": "Requested — BrandEx will confirm scope and price",
  "package.status.quoted": "Quoted — BrandEx will contact you to confirm",
  "package.status.active": "Active",
  "package.status.paused": "Paused",
  "package.status.cancelled": "Cancelled",
  "package.none": "You don't have a package yet. Choose the standard package or ask for a custom one.",
  "package.marketValue": "Market value of what is included: {value}",
  "package.choose": "Request this package",
  "package.custom": "Custom package",
  "package.customHint": "Increase or reduce any component. The team prepares a quotation after you submit.",
  "package.customNote": "Anything else we should know?",
  "package.requestCustom": "Request a custom quote",
  "package.requested": "Request sent. BrandEx will confirm scope and price.",
  "package.askChange": "Ask for a change",
  "package.askChangeLabel": "What would you like to add or reduce?",
  "package.send": "Send to BrandEx",
  "package.changeSent": "Sent. The team will get back to you.",
  "package.previousRequests": "Your requests",
  "package.paymentNote": "Payments are recorded by the BrandEx team. Online payment is coming later.",
  "nav.workspace": "Workspace",
  "greeting.morning": "Good morning, {name}",
  "greeting.afternoon": "Good afternoon, {name}",
  "greeting.evening": "Good evening, {name}",
  "comingSoon.research": "See what patients in your area are searching for and asking, so every piece of content answers a real question.",
  "comingSoon.studio": "Book studio sessions and follow recordings from raw footage to finished edit.",
  "comingSoon.approvals": "Review drafts from the BrandEx team and approve or request changes — nothing is published without you.",
  "comingSoon.ads": "Campaigns managed by our team under a separate agreement, with spend caps and your approval before launch.",
  "comingSoon.google": "Set up and keep your Google Business Profile accurate, with reviews in one place.",
  "comingSoon.bookings": "Let patients find you and book appointments online.",
  "comingSoon.ethics": "Checks for medical advertising rules and patient privacy before anything goes live.",

  "overview.welcome": "Welcome, {name}",
  "overview.completeness": "Profile completeness",
  "overview.nextSteps": "Recommended next steps",
  "overview.step.verifyEmail": "Verify your email address",
  "overview.step.addPhone": "Add a phone number",
  "overview.step.enableMfa": "Turn on two-factor authentication",
  "overview.step.inviteTeam": "Invite a teammate",
  "overview.resendVerification": "Resend verification email",
  "overview.verificationSent": "Verification email sent.",
  "overview.metricsNote": "Figures are calculated by BrandEx from your account data.",
  "dash.awaiting": "Waiting for your approval",
  "dash.bookings": "New booking requests",
  "dash.published": "Published this month",
  "dash.attention": "Needs your attention",
  "dash.nothing": "Nothing needs you right now.",
  "dash.approve": "Approve",
  "dash.review": "Review",
  "dash.confirm": "Confirm",
  "dash.decline": "Decline",
  "dash.more": "{n} more",
  "dash.portfolio": "Your portfolio",
  "dash.finishProfile": "Finish your profile",
  "dash.finishProfileHint": "Set up your profile, chambers and package to get your website and content plan.",
  "dash.editProfile": "Edit profile",
  "dash.nextShoot": "Next shoot",
  "dash.noShoot": "No shoot scheduled yet.",
  "dash.fullSchedule": "Full schedule",
  "dash.studio": "BrandEx studio",
  "dash.package": "This month's package",
  "dash.packageHint": "Approved or published, out of what your package includes.",
  "dash.noPackage": "Progress appears once BrandEx plans your month.",
  "dash.withYou": "{n} waiting for you",
  "overview.organization": "Organization",
  "overview.role": "Your role",

  "comingSoon.title": "Coming in a later release",
  "comingSoon.inbox": "The unified inbox will bring website chat, WhatsApp, Messenger, Instagram and Telegram into one place.",
  "comingSoon.services": "Browse managed-service packages and track projects with the BrandEx team.",
  "comingSoon.profile": "Build your public professional profile and connect social accounts.",

  "security.sessions": "Signed-in devices",
  "security.thisDevice": "This device",
  "security.signOutDevice": "Sign out",
  "security.signOutAll": "Sign out everywhere",
  "security.changePassword": "Change password",
  "security.currentPassword": "Current password",
  "security.passwordChanged": "Password changed. Other devices were signed out.",
  "security.mfa": "Two-factor authentication",
  "security.mfaOn": "On — your account is protected with an authenticator app.",
  "security.mfaOff": "Off",
  "security.mfaStart": "Set up authenticator app",
  "security.mfaScan": "Scan the QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) or type the setup key, then enter the 6-digit code it shows.",
  "security.mfaQr": "QR code for your authenticator app",
  "security.mfaKeyHint": "Choose a time-based key. This key stays the same until you confirm, even if you navigate away — but reloading the page creates a new one, so scan it in one go.",
  "security.mfaKey": "Setup key",
  "security.mfaConfirm": "Confirm",
  "security.mfaCodeRejected": "That code does not match the setup key on this screen. If you already added Brandex to your authenticator, remove those entries and scan this QR again. Also check that your phone's date and time are set automatically.",
  "security.mfaEnabled": "Two-factor authentication is on.",
  "security.lastActive": "Last active {time}",

  "team.members": "Members",
  "team.role": "Role",
  "team.joined": "Joined",
  "team.roleUpdated": "Role updated.",
  "role.owner": "Owner",
  "role.admin": "Admin",
  "role.manager": "Manager",
  "role.agent": "Agent",
  "role.viewer": "Viewer",
  "role.assistant": "Assistant",

  "onboarding.title": "Set up your BrandEx profile",
  "onboarding.step": "Step {n} of {total}",
  "onboarding.savedLocally": "Your progress is saved on this device until you finish.",
  "onboarding.steps.account": "Your account",
  "onboarding.steps.practice": "Practice",
  "onboarding.steps.qualifications": "Qualifications",
  "onboarding.steps.chambers": "Chambers and booking",
  "onboarding.steps.package": "Package",
  "onboarding.steps.brand": "Content and brand",
  "onboarding.fullName": "Full name",
  "onboarding.fullNameHint": "As patients should see it, e.g. Dr. Ayesha Rahman.",
  "onboarding.phone": "Mobile number",
  "onboarding.phoneHint": "e.g. 01712-345678. Used for booking alerts.",
  "onboarding.specialty": "Specialty",
  "onboarding.designation": "Designation and workplace",
  "onboarding.designationHint": "e.g. Consultant, Sample Hospital",
  "onboarding.registration": "Medical council registration no. (optional)",
  "onboarding.registrationHint": "Optional now, but needed to publish your website: BrandEx checks it on the BMDC register.",
  "onboarding.languages": "Languages you consult in",
  "onboarding.degrees": "Degrees",
  "onboarding.degreeTitle": "Degree {n}",
  "onboarding.degreeInstitution": "Institution (optional)",
  "onboarding.degreeYear": "Year (optional)",
  "onboarding.addDegree": "Add degree",
  "onboarding.removeDegree": "Remove degree {n}",
  "onboarding.bio": "About you, in two or three lines",
  "onboarding.bioHint": "{count} characters. Between 20 and 600; a good bio is 150 to 400.",
  "onboarding.chamber": "Chamber {n}",
  "onboarding.chamberName": "Name",
  "onboarding.chamberAddress": "Address",
  "onboarding.days": "Days",
  "onboarding.from": "From",
  "onboarding.to": "To",
  "onboarding.minutes": "Minutes per patient",
  "onboarding.minutesValue": "{n} min",
  "onboarding.addChamber": "Add another chamber",
  "onboarding.removeChamber": "Remove chamber {n}",
  "onboarding.location": "Map location (optional)",
  "onboarding.location.hint": "Paste the chamber's Google Maps link or type its coordinates. Patients get directions from where they are.",
  "onboarding.location.set": "On the map at {lat}, {lng}.",
  "onboarding.location.placeholder": "https://www.google.com/maps/place/… or 23.7808, 90.4073",
  "onboarding.location.here": "I'm at the chamber: use my location",
  "onboarding.location.find": "Find it on Google Maps",
  "onboarding.location.check": "Check on the map",
  "onboarding.location.clear": "Remove",
  "onboarding.location.denied": "Location access was blocked. Allow it in the browser, or paste a Google Maps link instead.",
  "onboarding.location.unavailable": "This device couldn't find its location. Paste a Google Maps link instead.",
  "onboarding.error.location": "Paste a Google Maps link to the chamber, or coordinates like 23.7808, 90.4073.",
  "onboarding.error.shortLink": "Short links can't be read. Open it, then copy the full address from the browser bar.",
  "onboarding.autoConfirm": "Confirm appointments automatically when the slot is free",
  "onboarding.packageIntro": "Choose a package created by the BrandEx team, or ask for a custom quote.",
  "onboarding.includesWebsite": "Includes your doctor website",
  "onboarding.noWebsite": "No website",
  "onboarding.addOn": "Add: {name}",
  "onboarding.selected": "Selected",
  "onboarding.select": "Select",
  "onboarding.noPlans": "No packages are on offer right now. Ask for a custom quote below.",
  "onboarding.accent": "Portfolio accent colour",
  "onboarding.shootType": "Preferred shoot type",
  "onboarding.back": "Back",
  "onboarding.continue": "Continue",
  "onboarding.createAccount": "Create account and continue",
  "onboarding.build": "Build my profile",
  "onboarding.signInInstead": "Already registered? Sign in",
  "onboarding.error.required": "Please fill in this field.",
  "onboarding.error.bio": "Write between 20 and 600 characters.",
  "onboarding.error.days": "Choose at least one day.",
  "onboarding.error.hours": "Closing time must leave room for at least one patient.",
  "onboarding.error.languages": "Choose at least one language.",
  "onboarding.error.package": "Choose a package or ask for a custom quote.",
  "onboarding.error.custom": "Keep at least one item in your custom package.",
  "onboarding.error.phone": "Enter a mobile number like 01712-345678 or +8801712345678.",
  "onboarding.error.email": "Enter a valid email address.",
  "onboarding.error.year": "Use a four-digit year from 1950 to next year.",
  "onboarding.error.server": "Please correct this field.",
  "onboarding.error.serverDetail": "Please correct the highlighted field: {detail}.",
  "onboarding.done.title": "Everything is set up",
  "onboarding.done.subtitle": "Here is what BrandEx created for you.",
  "onboarding.done.account": "Account created for {email}",
  "onboarding.done.verify": "Check your inbox to verify your email.",
  "onboarding.done.profile": "Profile: {name} · {specialty}",
  "onboarding.done.chambers": "Chambers with weekly hours: {count}",
  "onboarding.done.autoConfirm": "Free slots are confirmed automatically",
  "onboarding.done.manualConfirm": "You confirm each appointment yourself",
  "onboarding.done.package": "Package requested: {name}",
  "onboarding.done.packageCustom": "Custom package requested. BrandEx will send a quote.",
  "onboarding.done.brand": "Accent colour and preferred shoot type saved",
  "onboarding.done.go": "Go to my dashboard",
  "specialty.gynaecology_obstetrics": "Gynaecology & Obstetrics",
  "specialty.paediatrics": "Paediatrics",
  "specialty.medicine": "Medicine",
  "specialty.cardiology": "Cardiology",
  "specialty.dermatology": "Dermatology",
  "specialty.orthopaedics": "Orthopaedics",
  "specialty.other": "Other",
  "language.bangla": "Bangla",
  "language.english": "English",
  "language.hindi": "Hindi",
  "language.arabic": "Arabic",
  "day.sat": "Sat",
  "day.sun": "Sun",
  "day.mon": "Mon",
  "day.tue": "Tue",
  "day.wed": "Wed",
  "day.thu": "Thu",
  "day.fri": "Fri",
  "accent.azure": "Azure",
  "accent.indigo": "Indigo",
  "accent.teal": "Teal",
  "accent.emerald": "Emerald",
  "accent.violet": "Violet",
  "accent.rose": "Rose",
  "accent.amber": "Amber",
  "accent.slate": "Slate",
  "shoot.in_studio": "In studio",
  "shoot.in_person": "In person (my chamber)",
  "shoot.podcast": "Podcast",
  "error.phone_taken": "This mobile number is already registered.",
  "nav.website": "My website",
  "nav.visitors": "Website visitors",
  "website.title": "My website",
  "website.view": "View my site",
  "website.state.awaiting_verification": "Waiting for your BMDC registration check",
  "verification.notSubmitted": "Add your BMDC registration number to publish your website.",
  "verification.pending": "BrandEx is checking BMDC registration {number}. Your website goes live once it is verified.",
  "verification.rejected": "We couldn't verify your BMDC registration: {reason}. Please check the number.",
  "verification.verified": "BMDC registration {number} verified.",
  "verification.fix": "Update registration number",
  "notify.registration_verified": "Your BMDC registration is verified. Your website can go live.",
  "notify.registration_rejected": "We couldn't verify your BMDC registration: {reason}",
  "notify.article_submitted": "{author} sent an article for your review: {title}",
  "notify.article_submitted_plain": "An article is waiting for your review: {title}",
  "notify.article_changes_requested": "The doctor asked for changes to your article: {title}",
  "notify.article_published": "Your article is live on the doctor's website: {title}",
  "portfolio.verified": "BMDC registration verified",
  "website.state.live": "Live",
  "website.state.awaiting_activation": "Reserved — goes live when your package is activated",
  "website.state.not_included": "Not included in your package",
  "website.state.offline": "Offline",
  "website.state.needs_onboarding": "Finish onboarding to create your site",
  "package.payment": "Payment",
  "package.noInvoices": "No invoices yet. BrandEx bills your package each month.",
  "package.status": "Status",
  "package.dueOn": "due {date}",
  "package.month": "Month",
  "package.amount": "Amount",
  "package.paidOn": "Paid",
  "package.invoice.due": "Due",
  "package.invoice.paid": "Paid",
  "package.invoice.overdue": "Overdue",
  "package.invoice.void": "Cancelled",
  "package.method.bank_transfer": "Bank transfer",
  "package.method.bkash": "bKash",
  "package.method.nagad": "Nagad",
  "package.method.cash": "Cash",
  "package.method.card": "Card",
  "package.prepaid": "Prepaid {months} months · saved {discount}",
  "package.refunded": "Refunded {amount}",
  "package.prepayOffer": "Pay 6 months ahead and save 5%, or 12 months and save 10%. Ask BrandEx for a prepaid invoice.",
  "package.cancel.title": "Cancel your package",
  "package.cancel.hint": "You can cancel any time. Your package runs to the end of this month and nothing is billed after. Your website stays online until then.",
  "package.cancel.start": "Cancel package",
  "package.cancel.confirm": "Cancel at the end of this month? Unused prepaid months are refunded, less the prepayment discount.",
  "package.cancel.yes": "Yes, cancel at month end",
  "package.cancel.no": "Keep my package",
  "package.cancel.ending": "Your package ends after {date}. Nothing is billed after that.",
  "package.cancel.keep": "Keep my package",
  "package.cancel.withdrawTitle": "Withdraw your request",
  "package.cancel.withdrawHint": "Nothing has been billed yet.",
  "package.cancel.withdraw": "Withdraw request",
  "package.cancel.withdrawConfirm": "Withdraw this package request?",
  "package.cancel.withdrawYes": "Yes, withdraw",
  "package.quoteValidUntil": "This quote is valid until {date}.",
  "package.quoteExpired": "This quote expired on {date}. BrandEx will send you a new one.",
  "package.priceLockedUntil": "Your price is locked until {date}.",
  "package.method.other": "Other",
  "notify.payment_due": "Your invoice for {period} is ready: {amount}, due {due_on}",
  "notify.payment_overdue": "Your payment for {period} is overdue: {amount}",
  "notify.payment_received": "Payment received for {period}: {amount}. Thank you.",
  "notify.payment_late": "{doctor}'s invoice for {period} ({amount}) is {days} days overdue",
  "notify.refund_issued": "BrandEx refunded {amount} for {period}. It reaches your original payment method within 10 working days.",
  "notify.package_cancelled": "A practice cancelled its package",
  "notify.package_cancelled_until": "A practice cancelled its package; it runs until {ends_at}",
  "notify.package_kept": "A practice withdrew its cancellation",
  "notify.monthly_report_ready": "Your report for {month} is ready",
  "report.title": "Monthly report",
  "report.intro": "What BrandEx delivered for you this month, from our own records.",
  "report.prev": "Previous month",
  "report.next": "Next month",
  "report.inProgress": "In progress",
  "report.final": "Final",
  "report.inProgressHint": "This month is still running. Figures update as work is approved and published.",
  "report.finalHint": "Closed on the 1st. This report no longer changes.",
  "report.calculated": "Calculated by BrandEx",
  "report.summary": "Summary",
  "report.approved": "Items approved",
  "report.published": "Posts published",
  "report.appointments": "Booking requests",
  "report.confirmed": "Appointments confirmed",
  "report.shootsHeld": "Shoots held",
  "report.delivery": "Delivered vs package",
  "report.kind": "Item",
  "report.committed": "In package",
  "report.delivered": "Delivered",
  "report.publishedCol": "Published",
  "report.waiting": "With you",
  "report.shortfall.awaiting_doctor": "Waiting for your approval",
  "report.shortfall.in_production": "BrandEx is still producing",
  "report.onTrack": "Complete",
  "report.noDelivery": "Nothing was planned for this month.",
  "report.posts": "Published posts",
  "report.noPosts": "No posts went live this month.",
  "report.bookings": "Bookings by chamber",
  "report.bookingsNote": "Counts only. Patient details are never included.",
  "report.declined": "Declined",
  "report.platforms": "Platform numbers",
  "report.platformsNote": "Reach and views appear only from connected platforms, labelled with their source. We never estimate.",
  "report.notConnected": "Not connected",
  "report.download": "Download PDF",
  "report.followers": "{n} followers",
  "report.views": "{n} views",
  "report.postsCount": "{n} posts",
  "report.unavailable": "Couldn't reach the platform",
  "report.source": "Source: {source}, {when}",
  "report.nextMonth": "Next month",
  "report.planned": "{n} items already planned",
  "report.noShoots": "No shoots scheduled yet.",
  "notify.bell": "Notifications",
  "notify.bellUnread": "Notifications, {n} unread",
  "notify.title": "Notifications",
  "notify.readAll": "Mark all read",
  "notify.empty": "You're all caught up.",
  "notify.content_sent_to_you": "“{title}” is waiting for your approval",
  "notify.content_approved": "{doctor} approved “{title}”",
  "notify.changes_requested": "{doctor} asked for changes to “{title}”: {comment}",
  "notify.changes_requested_plain": "{doctor} asked for changes to “{title}”",
  "notify.content_published": "“{title}” is live on {platform}",
  "notify.shoot_scheduled": "Shoot booked: {shoot}, {when}",
  "notify.shoot_cancelled": "Shoot cancelled: {shoot}, {when}",
  "notify.new_booking": "New booking: {patient}, {when} at {chamber}",
  "notify.booking_cancelled_by_patient": "{patient} cancelled their booking for {when} at {chamber}",
  "notify.booking_rescheduled": "{patient} moved their booking to {when} at {chamber}",
  "notify.template_requested": "{doctor} asked for a custom website template",
  "notify.template_request_updated.in_progress": "Our design team started on your custom template",
  "notify.template_request_updated.delivered": "Your custom template is ready in your Brand kit",
  "notify.practice_updated_by_staff.profile": "BrandEx updated your profile and portfolio",
  "notify.practice_updated_by_staff.brand": "BrandEx updated your brand kit",
  "notify.practice_updated_by_staff.bookings": "BrandEx updated your booking settings",
  "notify.practice_updated_by_staff.website": "BrandEx changed whether your website is published",
  "notify.social_reconnect_needed": "Your {platform} stopped syncing. Reconnect it to keep your website up to date",
  "notify.template_request_updated.declined": "We couldn't take on your custom template request: {note}",
  "bookings.title": "Bookings",
  "bookings.subtitle": "How patients book you, who is told, and your appointments.",
  "bookings.method": "Booking method",
  "bookings.method.built_in": "BrandEx booking (built in)",
  "bookings.method.built_in.hint": "Patients pick a chamber, day and time on your portfolio. Each booking is saved on the BrandEx server automatically.",
  "bookings.method.calendly": "Calendly",
  "bookings.method.calendly.hint": "Use your own Calendly page. Patients book on Calendly and your portfolio links to it.",
  "bookings.method.contact": "Call or WhatsApp (recommended)",
  "bookings.method.contact.hint": "Patients tap Book on your website and see your numbers. They call or message you, and your chamber books the time.",
  "bookings.contactPhone": "Number patients call",
  "bookings.contactPhoneHint": "Shown on your website, e.g. 01712-345678.",
  "bookings.contactWhatsapp": "WhatsApp number for patients",
  "bookings.contactWhatsappHint": "Shown on your website. Leave empty if you don't take bookings on WhatsApp.",
  "bookings.sameNumber": "Same as the phone number",
  "bookings.contactNeeded": "Booking is on. Add a phone or WhatsApp number so patients can reach you from the Book button.",
  "bookings.publicContact": "Shows Book buttons on your public site. Patients tap one and choose to call or message you on WhatsApp.",
  "bookings.calendly": "Your Calendly link",
  "bookings.whatsapp": "WhatsApp for new-booking alerts",
  "bookings.whatsappHint": "Saved now. WhatsApp alerts start when BrandEx connects WhatsApp.",
  "bookings.email": "Email for new bookings",
  "bookings.emailHint": "We email you each new booking.",
  "bookings.public": "Patients can book from your portfolio",
  "bookings.publicHint": "Shows Book buttons on your public site that open the booking form.",
  "bookings.publicCalendly": "Shows a button to your Calendly page on your public site.",
  "bookings.autoConfirm": "Confirm free slots automatically",
  "bookings.autoConfirmHint": "Off: you confirm or decline each request.",
  "bookings.save": "Save booking settings",
  "bookings.saved": "Booking settings saved.",
  "bookings.appointments": "Appointments",
  "bookings.appointmentsHint": "From today on. Patients book from your public portfolio.",
  "bookings.none": "No upcoming appointments.",
  "bookings.when": "When",
  "bookings.patient": "Patient",
  "bookings.chamber": "Chamber",
  "bookings.reason": "Reason",
  "bookings.status": "Status",
  "bookings.state.pending": "Waiting for you",
  "bookings.state.confirmed": "Confirmed",
  "bookings.state.declined": "Declined",
  "bookings.state.cancelled": "Cancelled",
  "bookings.confirm": "Confirm",
  "bookings.decline": "Decline",
  "bookings.cancel": "Cancel",
  "bookings.confirmFor": "Confirm {name}",
  "bookings.declineFor": "Decline {name}",
  "bookings.cancelFor": "Cancel {name}",
  "bookings.availability": "Weekly availability",
  "bookings.availabilityHint": "Slots come from your chambers' days and hours. Change them in",
  "bookings.fee": "Consultation fee (৳)",
  "bookings.feeHint": "Shown on your portfolio. Leave empty to hide it.",
  "bookings.onlinePayment": "Patients pay the fee online when booking",
  "bookings.onlinePaymentHint": "The time is held for 30 minutes while they pay. You're told once it's paid.",
  "bookings.onlinePaymentOff": "Online payment isn't available yet. Patients pay at the chamber.",
  "bookings.onlinePaymentSandbox": "Test mode: payments go through a sandbox and no money moves.",
  "bookings.reference": "Ref.",
  "bookings.payment.pending": "Awaiting payment",
  "bookings.payment.paid": "Paid {amount}",
  "bookings.payment.failed": "Payment failed",
  "bookings.payment.expired": "Payment expired",
  "bookings.payment.refund_due": "Refund {amount}",
  "bookings.cancelledBy.patient": "by the patient",
  "bookings.cancelledBy.practice": "by the chamber",
  "bookings.cancelledBy.unpaid": "not paid in time",
  "bookings.moved": "Moved {n}×",
  "bookings.timeOff": "Time off",
  "bookings.timeOffHint": "Block days or hours when you aren't seeing patients. Those times stop being offered.",
  "bookings.timeOff.chamber": "Chamber",
  "bookings.timeOff.allChambers": "All chambers",
  "bookings.timeOff.from": "From",
  "bookings.timeOff.until": "Until",
  "bookings.timeOff.note": "Note (only you see it)",
  "bookings.timeOff.add": "Block this time",
  "bookings.timeOff.none": "No time off planned.",
  "bookings.timeOff.remove": "Remove",
  "bookings.timeOff.removeFor": "Remove time off {range}",
  "bookings.timeOff.badRange": "The end must be after the start.",
  "bookings.timeOff.affected": "{n} booking(s) fall in this time. They aren't cancelled: confirm, cancel or call these patients.",
  "nav.content": "Content and approvals",
  "content.title": "Content and approvals",
  "content.subtitle": "Everything BrandEx makes for you this month. Review what we send, approve it or ask for changes.",
  "content.waiting": "{n} item(s) are waiting for your approval.",
  "content.filter": "Filter content",
  "content.tab.all": "All",
  "content.tab.in_production": "In production",
  "content.month": "Month",
  "content.allMonths": "All months",
  "content.waitingTitle": "Waiting for your approval",
  "content.waitingHint": "Open the preview, then approve it or tell the team what to change. Nothing is published without your approval.",
  "content.allClear": "You're all caught up: nothing is waiting for your approval.",
  "content.allTitle": "Your content",
  "content.noneInTab": "Nothing here right now.",
  "content.progress": "Progress",
  "content.stage.plan": "Planned",
  "content.stage.production": "In production",
  "content.stage.review": "Your review",
  "content.stage.approved": "Approved",
  "content.stage.live": "Live",
  "content.count": "{n} items",
  "content.empty": "Your content plan appears here once BrandEx plans your month.",
  "content.footer": "Approvals are recorded with your name and time. BrandEx cannot publish an item until you approve it.",
  "content.due": "due {date}",
  "content.revision": "version {n}",
  "content.caption": "Caption",
  "content.preview": "Open preview",
  "content.approve": "Approve",
  "content.approveItem": "Approve {title}",
  "content.requestChanges": "Request changes",
  "content.whatToChange": "What should change?",
  "content.whatToChangeHint": "Be specific: the team works from this note.",
  "content.send": "Send",
  "content.publishedOn": "Published on",
  "content.doctorsDiary": "Doctors Diary",
  "content.yours": "Your",
  "content.platform.website": "Your website",
  "content.platform.facebook": "Facebook",
  "content.platform.instagram": "Instagram",
  "content.platform.youtube": "YouTube",
  "content.platform.tiktok": "TikTok",
  "content.platform.linkedin": "LinkedIn",
  "content.platform.google": "Google",
  "content.shoot.next": "Next shoot",
  "content.shoot.studio": "BrandEx studio",
  "content.shoot.chamber": "Your chamber",
  "content.shoot.scheduled": "Scheduled",
  "content.shoot.kind.podcast": "Podcast",
  "content.shoot.kind.promo_shoot": "Promo shoot",
  "content.shoot.kind.in_person": "In-person shoot",
  "content.shoot.kind.photo_session": "Photo session",
  "schedule.subtitle": "Recording days BrandEx has booked with you and when content is due, in Bangladesh time.",
  "schedule.filter": "Show",
  "schedule.filter.all": "Everything",
  "schedule.filter.shoots": "Shoots",
  "schedule.filter.content": "Content due",
  "schedule.today": "Today",
  "schedule.minutes": "{n} min",
  "schedule.due": "{kind} due",
  "schedule.empty": "Nothing scheduled yet. Shoots BrandEx books with you and content due dates appear here.",
  "content.history": "History",
  "content.by.doctor": "by you",
  "content.by.team": "by BrandEx",
  "content.by.system": "by BrandEx",
  "content.status.planned": "Planned",
  "content.status.shot": "Shot",
  "content.status.editing": "Editing",
  "content.status.internal_review": "Internal review",
  "content.status.sent_to_doctor": "Waiting for you",
  "content.status.changes_requested": "Changes requested",
  "content.status.approved": "Approved",
  "content.status.published": "Published",
  "content.kind.podcast": "Podcast",
  "content.kind.reel": "Reel",
  "content.kind.photo_card": "Photo card",
  "content.kind.promo_shoot": "Promo shoot",
  "content.kind.article": "Article",
  "content.kind.website_section": "Website section",
  "content.kind.other": "Other",
  "nav.brand": "Brand kit",
  "brand.title": "Brand kit",
  "brand.subtitle": "How your brand looks on your portfolio and in the content BrandEx makes for you.",
  "brand.preview": "Preview of your portfolio header",
  "brand.accentHint": "Used on your public portfolio. Choose from the BrandEx-safe palette.",
  "brand.images": "Photo and logo",
  "brand.imagesHint": "PNG, JPEG or WebP up to 5 MB. We resize them and remove location data from phone photos.",
  "brand.slot.photo": "Profile photo",
  "brand.slot.logo": "Logo",
  "brand.slot.clinic_logo": "Clinic logo (optional)",
  "brand.photoHint": "A clear, recent portrait; at least 200 × 200 pixels.",
  "brand.logoHint": "A transparent PNG looks best; at least 64 pixels.",
  "brand.upload": "Upload",
  "brand.replace": "Replace",
  "brand.remove": "Remove {slot}",
  "brand.tooBig": "That file is larger than 5 MB. Choose a smaller image.",
  "brand.content": "Content preferences",
  "brand.toneHint": "Captions written for you follow your tone and always need your approval.",
  "brand.tone": "Tone for captions",
  "brand.tone.warm_simple": "Warm and simple",
  "brand.tone.professional_formal": "Professional and formal",
  "brand.tone.educational": "Educational",
  "brand.save": "Save brand kit",
  "brand.website": "Website",
  "brand.websiteHint": "Choose how your website is laid out. Every template uses your accent colour, photo and the same verified information.",
  "brand.template": "Website template",
  "brand.template.modern": "Modern",
  "brand.template.modern.hint": "Large portrait beside your name; booking up front.",
  "brand.template.classic": "Classic",
  "brand.template.classic.hint": "Centred, traditional clinic look with a colour band.",
  "brand.template.minimal": "Minimal",
  "brand.template.minimal.hint": "Clean and typographic; colour only where it matters.",
  "brand.template.editorial": "Editorial",
  "brand.template.editorial.hint": "Your videos and posts lead, like a health magazine.",
  "brand.template.clinical": "Clinical",
  "brand.template.clinical.hint": "Crisp and practical; chambers and hours right in the header.",
  "brand.template.elegant": "Elegant",
  "brand.template.elegant.hint": "Deep, premium header with serif headings and an arched portrait.",
  "brand.template.bold": "Bold",
  "brand.template.bold.hint": "Your colour fills the header; big type and numbered sections.",
  "brand.template.warm": "Warm",
  "brand.template.warm.hint": "Soft, friendly and rounded; ideal for family practice.",
  "brand.template.spotlight": "Spotlight",
  "brand.template.spotlight.hint": "Your photo as a full-width banner with your name over it.",
  "brand.template.custom": "Custom: {name}",
  "brand.template.custom.hint": "Your own design, made for your practice by BrandEx.",
  "brand.slot.favicon": "Site icon (favicon)",
  "brand.faviconHint": "A square image, at least 48 × 48 pixels, shown in the browser tab. BrandEx's icon is used until you add one.",
  "brand.custom.title": "Custom template",
  "brand.custom.hint": "Want something unique? Tell our design team what you like and we'll build a template for your practice.",
  "brand.custom.brief": "What would you like?",
  "brand.custom.briefHint": "Style, colours, the feeling you want, anything to avoid (at least 20 characters).",
  "brand.custom.references": "Sites you like (optional)",
  "brand.custom.referencesHint": "Up to 5 https:// links, one per line.",
  "brand.custom.send": "Send request",
  "brand.custom.sent": "Request sent. Our design team will get back to you.",
  "brand.custom.requests": "Your requests",
  "brand.custom.status.open": "Sent",
  "brand.custom.status.in_progress": "In design",
  "brand.custom.status.delivered": "Delivered",
  "brand.custom.status.declined": "Declined",
  "brand.custom.status.cancelled": "Withdrawn",
  "brand.custom.cancel": "Withdraw",
  "brand.custom.note": "From BrandEx: {note}",
  "brand.custom.ready": "Your custom template “{name}” is ready. Choose it under Website template.",
  "brand.custom.openRequest": "Your request is with our design team. You can send another once it's finished.",
  "brand.custom.badBrief": "Tell us a little more (at least 20 characters).",
  "brand.custom.badLinks": "Use up to 5 full https:// links.",
  "brand.showPosts": "Show my posts on my website",
  "brand.showPostsHint": "Content BrandEx published for you (videos, reels, articles) appears on your site with links to where it is live.",
  "brand.websiteDelay": "Saved changes show on your website right away.",
  "nav.portfolio": "Profile and portfolio",
  "website.state.unpublished": "Unpublished — only you can see it",
  "profile.title": "Profile and portfolio",
  "profile.preview": "Preview portfolio",
  "profile.publish": "Publish",
  "profile.unpublish": "Unpublish",
  "profile.unpublishedHint": "Your portfolio is hidden from patients and search engines. Publish it again whenever you are ready.",
  "profile.complete": "{percent}% profile complete",
  "profile.saved": "Saved. Your portfolio shows the change right away.",
  "profile.details": "Your details",
  "profile.detailsHint": "What patients read on your portfolio. Your name, specialty and degrees build trust; keep them accurate.",
  "profile.saveDetails": "Save changes",
  "profile.chambers": "Chambers",
  "profile.chambersHint": "Where and when patients can see you. Bookings use these hours.",
  "profile.saveChambers": "Save chambers",
  "profile.faq": "FAQ for your portfolio",
  "profile.faqHint": "Write an answer or approve the draft. Only approved answers appear on the public page.",
  "profile.faqSuggest": "Add common questions",
  "profile.faqEmpty": "No questions yet. Add common questions for your specialty, or write your own.",
  "profile.faqAnswer": "Answer",
  "profile.faqAnswerHint": "Plain, general advice in two or three sentences. Never a diagnosis.",
  "profile.faqApprove": "Save and approve answer",
  "profile.faqSaveDraft": "Save draft",
  "profile.faqRemove": "Remove “{question}”",
  "profile.faqNew": "New question",
  "profile.faqNewPlaceholder": "A question patients often ask you",
  "profile.faqAdd": "Add question",
  "profile.faqStatus.approved": "Approved",
  "profile.faqStatus.draft": "Draft — not public",
  "profile.faqStatus.needs_answer": "Needs answer",
  "profile.completeTitle": "Complete your profile",
  "profile.completeHint": "Calculated by BrandEx from your profile.",
  "profile.step.bio": "Biography of 150 to 400 characters",
  "profile.step.degrees": "Degrees with institutions",
  "profile.step.chambers": "At least one chamber",
  "profile.step.registration_no": "Registration number",
  "profile.step.profile_photo": "Profile photo (Brand kit)",
  "profile.step.faq_answers": "At least 3 approved FAQ answers",
  "profile.step.published": "Portfolio published",
  "profile.visibilityTitle": "Search and AI visibility",
  "profile.visibilityHint": "Maintained by BrandEx. Rankings are not guaranteed; this is the on-page work.",
  "profile.check.title_and_description": "Page title and description",
  "profile.check.mobile_friendly": "Mobile-friendly layout",
  "profile.check.structured_data": "Doctor and FAQ structured data",
  "profile.check.faq_answers": "At least 3 approved FAQ answers",
  "profile.check.profile_photo": "Profile photo",
  "profile.check.sitemap": "Sitemap for search engines",
  "profile.step.services": "At least 3 services or conditions listed",
  "profile.step.experience": "Career experience added",
  "profile.check.social_profiles": "Social profiles linked",
  "profile.check.services_listed": "Services listed for search",
  "website.address": "Website address",
  "website.customDomain": "Custom domain",
  "website.noDomain": "Your site uses the BrandEx address. Ask the team to connect your own domain.",
  "website.domainConnected": "Connected by BrandEx. Point your domain's DNS to BrandEx as the team instructs.",
  "website.upgrade": "Doctor websites come with packages that include the web portfolio. Ask for a change on the Package page.",
  "website.offlineHint": "BrandEx has taken your site offline. Contact the team.",
  "website.awaitingHint": "We've reserved {url}. It goes live as soon as BrandEx activates your package.",
  "website.contents": "What patients see",
  "website.contentsHint": "Your site shows your profile, degrees, languages and chambers with weekly hours. Edit them in your profile.",
  "website.openPackage": "Open Package and billing",
  "portfolio.language": "Language",
  "portfolio.bookAppointment": "Book an appointment",
  "portfolio.background": "About",
  "portfolio.specialisation": "Specialisation",
  "portfolio.degrees": "Degrees and training",
  "portfolio.chambers": "Chambers and hours",
  "portfolio.languages": "Languages",
  "portfolio.registration": "Registration no. {value}",
  "portfolio.slot": "{n} minutes per patient",
  "portfolio.faq": "Frequently asked questions",
  "portfolio.faqNote": "General information, approved by the doctor. It is not a diagnosis; please see a doctor about your own health.",
  "portfolio.logo": "Logo",
  "portfolio.clinicLogo": "Clinic logo",
  "portfolio.chamberHours": "Chambers and weekly hours.",
  "portfolio.book.calendly": "Book on Calendly",
  "portfolio.book.contactTitle": "Call or WhatsApp to book",
  "portfolio.book.contactHint": "The chamber books your appointment by phone. Choose how you'd like to get in touch.",
  "portfolio.book.call": "Call to book",
  "portfolio.book.whatsapp": "Book on WhatsApp",
  "portfolio.book.whatsappText": "Hello, I found you on your website and would like to book an appointment.",
  "portfolio.book.contactSay": "Tell them which chamber and day suit you, and the reason for your visit.",
  "portfolio.book.chamber": "Chamber",
  "portfolio.book.day": "Day",
  "portfolio.book.time": "Time",
  "portfolio.book.noSlots": "No free times in the next two weeks. Please call the chamber.",
  "portfolio.book.name": "Your name",
  "portfolio.book.phone": "Mobile number",
  "portfolio.book.reason": "Reason for the visit (optional)",
  "portfolio.book.privacy": "Your name and number go only to the doctor's practice, to confirm the appointment.",
  "portfolio.book.submit": "Request appointment",
  "portfolio.book.sending": "Sending…",
  "portfolio.book.requested": "Request sent. The chamber will confirm your appointment.",
  "portfolio.book.confirmed": "Booked. Your appointment is confirmed.",
  "portfolio.book.taken": "Someone just booked that time. Please choose another.",
  "portfolio.book.tooMany": "Too many requests. Please try again later or call the chamber.",
  "portfolio.book.badPhone": "Enter a mobile number like 01712-345678.",
  "portfolio.book.badName": "Enter your name.",
  "portfolio.book.failed": "Couldn't send the request. Please try again.",
  "portfolio.nav.label": "Sections",
  "portfolio.nav.about": "About",
  "portfolio.nav.book": "Book",
  "portfolio.nav.chambers": "Chambers",
  "portfolio.nav.faq": "FAQ",
  "portfolio.credentials": "Qualifications",
  "portfolio.findChamber": "Find a chamber",
  "portfolio.consults": "Consults at",
  "portfolio.chamberCount": "{n} chamber(s)",
  "portfolio.atAGlance": "At a glance",
  "portfolio.weeklyDays": "Days open",
  "portfolio.closed": "Closed",
  "portfolio.directions": "Directions",
  "portfolio.hours.title": "Where & when",
  "portfolio.hours.openUntil": "Open now · until {time}",
  "portfolio.hours.opensToday": "Opens today at {time}",
  "portfolio.hours.opensOn": "Next: {day} at {time}",
  "portfolio.hours.directionsTo": "Directions to {name}",
  "portfolio.hours.more": "See all chambers (+{n})",
  "portfolio.affiliations": "Clinical practice & appointments",
  "portfolio.care.eyebrow": "Areas of care",
  "portfolio.care.title": "Wherever you are in your journey",
  "portfolio.care.lead": "What {name} looks after, explained plainly.",
  "portfolio.care.book": "Book a consultation",
  "portfolio.online.title": "A consultation from the comfort of home",
  "portfolio.online.lead": "Video consultations are available.",
  "portfolio.online.book": "Book a video consultation",
  "portfolio.articles.title": "Health articles",
  "portfolio.articles.lead": "Plain-language guidance, written and approved by the doctor.",
  "portfolio.articles.minutes": "{n} min read",
  "portfolio.social.title": "Watch and follow",
  "portfolio.social.lead": "Videos and posts from {name}'s channels.",
  "portfolio.social.tabs": "Channels",
  "portfolio.social.subscribers": "{n} subscribers",
  "portfolio.social.followers": "{n} followers",
  "portfolio.social.follow": "Follow on {platform}",
  "portfolio.social.play": "Play “{title}”",
  "portfolio.social.views": "{n} views",
  "portfolio.social.open": "Open on {platform}",
  "portfolio.social.reviews": "{n} Google reviews",
  "portfolio.social.allReviews": "See all reviews on Google",
  "portfolio.social.fromGoogle": "Reviews from Google, shown as their authors wrote them.",
  "portfolio.social.reply": "Reply from the practice:",
  "portfolio.social.likes": "{n} likes",
  "portfolio.nav.care": "Care",
  "portfolio.cta.title": "Ready to book your visit?",
  "portfolio.cta.body": "Choose a chamber and a time that suits you. {name}'s chamber confirms by phone.",
  "portfolio.disclaimer": "Information on this website is general and is not medical advice. In an emergency, go to the nearest hospital.",
  "portfolio.book.lead": "Choose the chamber that suits you. The chamber confirms your appointment by phone.",
  "portfolio.book.offline": "Online booking isn't open yet. Please visit or call one of the chambers below.",
  "portfolio.book.close": "Close",
  "portfolio.book.ctaHint": "Tap the button to see how to book: by phone, on WhatsApp or online.",
  "portfolio.book.calendlyHint": "Appointments for this doctor are booked on Calendly.",
  "portfolio.book.step.chamber": "Choose a chamber",
  "portfolio.book.step.time": "Pick a day and time",
  "portfolio.book.step.details": "Your details",
  "portfolio.book.morning": "Morning",
  "portfolio.book.afternoon": "Afternoon",
  "portfolio.book.evening": "Evening",
  "portfolio.book.slotsFree": "{n} times free",
  "portfolio.book.pickTime": "Choose a time for your appointment.",
  "portfolio.book.requestedNext": "You'll get a call on your mobile to confirm. Please keep it nearby.",
  "portfolio.book.confirmedNext": "Please arrive 10 minutes early and bring any previous reports.",
  "portfolio.book.another": "Book another appointment",
  "portfolio.book.email": "Email (optional)",
  "portfolio.book.emailHint": "We'll email your confirmation and a reminder the day before.",
  "portfolio.book.badEmail": "Enter a valid email, or leave it empty.",
  "portfolio.book.fee": "Consultation fee",
  "portfolio.book.feeOnline": "Paid online now to hold your time.",
  "portfolio.book.feeAtChamber": "Paid at the chamber.",
  "portfolio.book.submitPay": "Book and pay {fee}",
  "portfolio.book.toPayment": "Taking you to payment…",
  "portfolio.book.reference": "Your reference: {ref}",
  "portfolio.book.manage": "View, change or cancel your booking",
  "portfolio.book.manageHint": "Keep this link: it's how you change or cancel online.",
  "appointment.title": "Your appointment",
  "appointment.notFound": "We couldn't find this booking. Please check the link you were sent.",
  "appointment.reference": "Reference",
  "appointment.status.pending": "Waiting for the chamber to confirm",
  "appointment.status.confirmed": "Confirmed",
  "appointment.status.declined": "Not available. Please book another time.",
  "appointment.status.cancelled": "Cancelled",
  "appointment.cancelledBy.patient": "You cancelled this appointment.",
  "appointment.cancelledBy.practice": "The chamber cancelled this appointment.",
  "appointment.cancelledBy.unpaid": "The payment wasn't completed, so the time was released.",
  "appointment.when": "When",
  "appointment.where": "Where",
  "appointment.doctor": "Doctor",
  "appointment.payment.pending": "Payment pending. Your time is held until {time}.",
  "appointment.payment.paid": "Paid {amount}",
  "appointment.payment.failed": "The payment failed.",
  "appointment.payment.expired": "The payment wasn't completed in time.",
  "appointment.payment.refund_due": "Paid {amount}. It will be refunded.",
  "appointment.payNow": "Pay {amount} now",
  "appointment.change": "Change time",
  "appointment.cancel": "Cancel appointment",
  "appointment.cancelConfirm": "Cancel this appointment? The time will be offered to other patients.",
  "appointment.cancelYes": "Yes, cancel",
  "appointment.keep": "Keep it",
  "appointment.pickNew": "Choose a new time",
  "appointment.movesLeft": "You can change the time {n} more time(s).",
  "appointment.noMovesLeft": "You've already changed the time twice. Please call the chamber.",
  "appointment.tooLate": "Online changes close 2 hours before the appointment. Please call the chamber.",
  "appointment.moved": "Done. Your new time is {when}.",
  "appointment.cancelled": "Your appointment is cancelled.",
  "appointment.taken": "Someone just took that time. Please choose another.",
  "appointment.failed": "Something went wrong. Please try again.",
  "appointment.bookAgain": "Book again with {doctor}",
  "appointment.save": "Move to this time",
  "appointment.noSlots": "No other free times in the next two weeks. Please call the chamber.",
  "pay.sandbox.title": "Test payment",
  "pay.sandbox.banner": "Test mode: no money moves. This page stands in for the payment gateway.",
  "pay.sandbox.for": "Consultation with {doctor}",
  "pay.sandbox.amount": "Amount",
  "pay.sandbox.pay": "Pay {amount} (test)",
  "pay.sandbox.fail": "Fail the payment (test)",
  "pay.sandbox.settled": "This payment is already settled.",
  "pay.sandbox.notFound": "This test payment doesn't exist, or test payments are turned off.",
  "pay.sandbox.back": "Back to your appointment",
  "portfolio.nav.posts": "Videos & posts",
  "portfolio.posts.title": "Latest videos and posts",
  "portfolio.posts.lead": "Health information made with BrandEx and approved by the doctor.",
  "portfolio.posts.latest": "Latest",
  "showcase.title": "Portfolio highlights",
  "showcase.hint": "What patients look for before they book: what you treat, where you have worked, your recognition and where to follow you.",
  "showcase.headline": "Headline",
  "showcase.headlineHint": "One line under your name. {count}/{max}",
  "showcase.headlinePlaceholder": "Safe pregnancies, gentle care",
  "showcase.tagline": "Tagline (optional)",
  "showcase.taglineHint": "A few words above your headline, shown in small capitals. {count}/{max}",
  "showcase.taglinePlaceholder": "Expertise with empathy",
  "showcase.emphasisPreview": "On your site:",
  "showcase.careAreas": "Areas of care",
  "showcase.careAreasHint": "Up to 6 numbered cards on your home page: what you look after, in your own words.",
  "showcase.addCareArea": "Add an area of care",
  "showcase.removeCareArea": "Remove area of care {n}",
  "showcase.careTitle": "Area {n}",
  "showcase.careTitlePlaceholder": "Pregnancy & birth",
  "showcase.careDescription": "What you do here (optional)",
  "showcase.careDescriptionHint": "One or two plain sentences. {count}/{max}",
  "showcase.careDescriptionPlaceholder": "Antenatal visits, birth planning and care after your baby arrives.",
  "showcase.online": "Online consultations",
  "showcase.onlineHint": "Show patients that you also consult over video.",
  "showcase.onlineOffered": "I offer video consultations",
  "showcase.onlineNote": "Note (optional)",
  "showcase.onlineNoteHint": "When or for what, in one line.",
  "showcase.onlineNotePlaceholder": "Follow-ups and second opinions, Saturday evenings.",
  "showcase.onlineUrl": "Booking link (optional)",
  "showcase.onlineUrlHint": "Leave empty to use the booking on your site.",
  "showcase.since": "Experience: practising since",
  "showcase.sinceHint": "The year you started practising. Your site shows your years of experience from it, updated every year.",
  "showcase.sinceYears": "Shown on your site as {n}+ years of experience.",
  "showcase.services": "Services and conditions treated",
  "showcase.servicesHint": "The words patients search for. Shown on your site and to search engines.",
  "showcase.addService": "Add a service or condition",
  "showcase.servicePlaceholder": "e.g. High-risk pregnancy",
  "showcase.serviceCount": "{count} of {max}. Press Enter to add.",
  "showcase.removeService": "Remove {name}",
  "showcase.add": "Add",
  "showcase.suggestions": "Suggestions for your specialty",
  "showcase.suggest.gynaecology_obstetrics": "Antenatal care|High-risk pregnancy|Normal delivery|PCOS|Infertility|Menstrual problems|Menopause care",
  "showcase.suggest.paediatrics": "Newborn care|Vaccination|Child growth and nutrition|Fever and infections|Asthma in children|Developmental check-ups",
  "showcase.suggest.medicine": "Diabetes|High blood pressure|Thyroid disorders|Fever and infections|Health check-ups|Gastric problems",
  "showcase.suggest.cardiology": "Chest pain|High blood pressure|Heart failure|ECG and echocardiography|Heart attack care|Cholesterol management",
  "showcase.suggest.dermatology": "Acne|Eczema|Psoriasis|Hair loss|Skin allergies|Fungal infections",
  "showcase.suggest.orthopaedics": "Back pain|Knee pain|Fractures|Arthritis|Sports injuries|Joint replacement",
  "showcase.suggest.other": "Consultation|Health check-ups|Follow-up care",
  "showcase.experience": "Experience",
  "showcase.experienceHint": "Positions you have held, newest first.",
  "showcase.role": "Position",
  "showcase.rolePlaceholder": "Associate Professor",
  "showcase.organization": "Hospital or institution",
  "showcase.from": "From",
  "showcase.to": "To",
  "showcase.present": "Present",
  "showcase.current": "I work here now",
  "showcase.addExperience": "Add a position",
  "showcase.removeExperience": "Remove position {n}",
  "showcase.awards": "Awards and recognition",
  "showcase.awardsHint": "Awards, fellowships and honours.",
  "showcase.awardTitle": "Award",
  "showcase.awardIssuer": "Given by",
  "showcase.addAward": "Add an award",
  "showcase.removeAward": "Remove award {n}",
  "showcase.year": "Year",
  "showcase.memberships": "Professional memberships",
  "showcase.membershipsHint": "Medical societies and associations.",
  "showcase.membershipOrg": "Society or association",
  "showcase.membershipPlaceholder": "Bangladesh Medical Association",
  "showcase.membershipRole": "Role (optional)",
  "showcase.membershipRolePlaceholder": "Life member",
  "showcase.addMembership": "Add a membership",
  "showcase.removeMembership": "Remove membership {n}",
  "showcase.publications": "Publications and media",
  "showcase.publicationsHint": "Papers, books, TV and newspaper features, talks.",
  "showcase.publicationKind": "Type",
  "showcase.kind.journal": "Journal paper",
  "showcase.kind.book": "Book or chapter",
  "showcase.kind.media": "Media appearance",
  "showcase.kind.talk": "Talk or seminar",
  "showcase.venue.journal": "Journal",
  "showcase.venue.book": "Publisher",
  "showcase.venue.media": "Newspaper, channel or show",
  "showcase.venue.talk": "Event",
  "showcase.publicationTitle": "Title",
  "showcase.publicationUrl": "Link (optional)",
  "showcase.addPublication": "Add an item",
  "showcase.removePublication": "Remove item {n}",
  "showcase.links": "Social profiles",
  "showcase.linksHint": "Links help search engines and AI assistants recognise you.",
  "showcase.network.facebook": "Facebook",
  "showcase.network.youtube": "YouTube",
  "showcase.network.instagram": "Instagram",
  "showcase.network.linkedin": "LinkedIn",
  "showcase.network.x": "X (Twitter)",
  "showcase.network.tiktok": "TikTok",
  "showcase.network.website": "Other website",
  "showcase.save": "Save highlights",
  "showcase.fixErrors": "Please check the highlighted fields.",
  "showcase.error.headline": "Use at most 120 characters.",
  "showcase.error.tagline": "Use at most 60 characters.",
  "showcase.error.emphasis": "Put the words to emphasise between two *, like *gentle care*.",
  "showcase.error.tooLong": "This is too long.",
  "showcase.error.year": "Enter a year from 1950 to this year.",
  "showcase.error.order": "Must not be before the start year.",
  "showcase.error.required": "Fill this in, or remove the row.",
  "showcase.error.service": "Use 2 to 80 characters.",
  "showcase.error.duplicate": "This is already in the list.",
  "showcase.error.link": "Use a full https:// link.",
  "showcase.error.network": "Use the https:// link of your profile on this network.",
  "portfolio.nav.services": "Services",
  "portfolio.nav.experience": "Experience",
  "portfolio.services.title": "Services and conditions treated",
  "portfolio.services.lead": "What patients see {name} for.",
  "portfolio.experience": "Experience",
  "portfolio.present": "Present",
  "portfolio.years": "{n}+ years",
  "portfolio.yearsLabel": "In practice",
  "portfolio.experience.years": "{n}+",
  "portfolio.experience.label": "Years of experience",
  "portfolio.experience.since": "Since {year}",
  "portfolio.recognition": "Awards and recognition",
  "portfolio.memberships": "Professional memberships",
  "portfolio.publications": "Publications and media",
  "portfolio.kind.journal": "Paper",
  "portfolio.kind.book": "Book",
  "portfolio.kind.media": "Media",
  "portfolio.kind.talk": "Talk",
  "portfolio.open": "Open",
  "portfolio.follow": "Follow {name}",
  "portfolio.posts.on.website": "Read",
  "portfolio.posts.on.youtube": "Watch on YouTube",
  "portfolio.posts.on.facebook": "View on Facebook",
  "portfolio.posts.on.instagram": "View on Instagram",
  "portfolio.posts.on.tiktok": "Watch on TikTok",
  "portfolio.posts.on.linkedin": "Read on LinkedIn",
  "portfolio.nav.articles": "Articles",
  "portfolio.nav.social": "Social",
  "portfolio.blog.title": "Health articles",
  "portfolio.blog.lead": "Health information written with BrandEx and approved by the doctor.",
  "portfolio.blog.empty": "No articles yet. Come back soon.",
  "portfolio.blog.read": "Read article",
  "portfolio.blog.back": "All articles",
  "portfolio.blog.older": "Older articles",
  "portfolio.blog.newer": "Newer articles",
  "portfolio.blog.by": "By {name}",
  "portfolio.blog.home": "Back to {name}",
  "portfolio.blog.featured": "Latest article",
  "portfolio.blog.contents": "On this page",
  "portfolio.blog.share": "Share",
  "portfolio.blog.shareOn": "Share on {network}",
  "portfolio.blog.more": "More from {name}",
  "portfolio.blog.ctaTitle": "Questions about your own health?",
  "portfolio.blog.ctaBody": "Articles are general information. For advice about you, book a visit with {name}.",
  "portfolio.blog.medicalNote": "This article is general health information and is not a diagnosis. Please see a doctor about your own symptoms. In an emergency, go to the nearest hospital.",
  "portfolio.by": "Portfolio by BrandEx",
  "portfolio.notFound": "This doctor's page is not available.",
  "auth.choosePassword": "Choose your password",
  "auth.setupIntro": "The BrandEx team has set up your account and profile. Choose a password to sign in.",
  "auth.savePassword": "Save password",
  "auth.passwordSaved": "Password saved. Please sign in.",
  "clientOnboarding.title": "Onboard a client",
  "clientOnboarding.account": "Doctor's account",
  "clientOnboarding.inviteLanguage": "Language for the invite email and portal",
  "clientOnboarding.submit": "Create client and send invite",
  "nav.articles": "Articles",
  "nav.social": "Social media",
  "social.title": "Social media",
  "social.subtitle": "Connect your channels, see every post with its numbers, and pin the best ones to your website.",
  "social.subtitle.staff": "This client's channels: connect them, check their numbers and choose what their website shows. Every change is recorded.",
  "social.tabs": "Platforms",
  "social.overview": "Overview",
  "social.platform.youtube": "YouTube",
  "social.platform.facebook": "Facebook",
  "social.platform.instagram": "Instagram",
  "social.platform.tiktok": "TikTok",
  "social.platform.linkedin": "LinkedIn",
  "social.platform.google": "Google reviews",
  "social.connect.signIn": "Continue with {platform}",
  "social.connect.reconnect": "Reconnect",
  "social.connect.staffSignIn": "Sign in with an account that manages the client's {platform}, or ask the doctor to connect it from their portal.",
  "social.connect.inBrowser": "Finish signing in to {platform} in your browser. You'll choose the account there, in the web portal.",
  "social.callbackMissing.staff": "BrandEx's public API address isn't set yet: a super admin sets it under Social health → Integrations.",
  "social.connect.facebook.lead": "Sign in with Facebook and choose the practice's Page. Posts, reactions, comments and shares are read; nothing is ever posted.",
  "social.connect.instagram.lead": "Sign in with Facebook (Instagram professional accounts are linked to a Page) and choose the account. Posts and reels are read with their likes and comments.",
  "social.connect.tiktok.lead": "Sign in with the clinic's TikTok account. Public videos are read with their views, likes, comments and shares.",
  "social.connect.linkedin.lead": "Sign in with LinkedIn and choose the practice's Company Page. Posts are read with their likes and comments.",
  "social.connect.google.lead": "Sign in with the Google account that manages the chamber's Business Profile and choose the location. Every review is read with its stars and reply.",
  "social.stats.rating": "Average rating",
  "social.stats.reviews": "Reviews",
  "social.stats.synced": "Synced",
  "social.site.rating": "Show the average rating and number of reviews",
  "social.fill.highest_rated": "The best rated",
  "social.sort.highest_rated": "Best rated",
  "social.kind.review": "Review",
  "social.review.stars": "{n} of 5 stars",
  "social.review.by": "Review by {name}",
  "social.review.noText": "Stars only, no text.",
  "social.review.reply": "Your reply:",
  "social.choose.title": "Choose the {platform} account to connect",
  "social.choose.lead": "These are the accounts you can manage. Pick the practice's.",
  "social.choose.expired": "This sign-in expired. Start again with the Connect button.",
  "social.choose.connect": "Connect this account",
  "social.signIn.expired": "The sign-in took too long or was already used. Please try again.",
  "social.signIn.denied": "The sign-in was cancelled, so nothing was connected.",
  "social.signIn.failed": "The platform didn't accept the sign-in. Try again; if it keeps failing, check the guide below.",
  "social.signIn.no_accounts": "That account doesn't manage any page or profile we can connect. Check the guide below.",
  "social.signIn.dismiss": "Dismiss",
  "social.guide.facebook.sync.title": "Wait for the first sync",
  "social.guide.facebook.sync.body": "The Page's posts load with their reactions, comments and shares within minutes; new posts follow on their own.",
  "social.guide.instagram.sync.title": "Wait for the first sync",
  "social.guide.instagram.sync.body": "Posts and reels load with their likes and comments within minutes.",
  "social.guide.tiktok.renew.title": "Reconnect once a year",
  "social.guide.tiktok.renew.body": "TikTok's access lasts a year. We renew it daily until then, and ask you to reconnect when TikTok requires it.",
  "social.guide.linkedin.page.title": "Have a LinkedIn Company Page",
  "social.guide.linkedin.page.body": "LinkedIn only shares Company Pages' posts with apps, not personal profiles. Create a Page for the practice if there isn't one.",
  "social.guide.linkedin.admin.title": "Be a Page super admin",
  "social.guide.linkedin.admin.body": "The person who connects must be an admin of the Page (Page → Settings → Manage admins).",
  "social.guide.linkedin.connect.title": "Continue with LinkedIn",
  "social.guide.linkedin.connect.body": "Sign in, allow access and choose the Page. Access lasts 60 days and renews itself.",
  "social.guide.google.profile.title": "Have a verified Business Profile",
  "social.guide.google.profile.body": "Reviews come from the chamber's Google Business Profile (the listing on Google Maps). It must be verified.",
  "social.guide.google.manager.title": "Sign in as an owner or manager",
  "social.guide.google.manager.body": "Use the Google account that owns or manages the profile (Business Profile → ⋮ → Business Profile settings → People and access).",
  "social.guide.google.connect.title": "Continue with Google and choose the location",
  "social.guide.google.connect.body": "Allow BrandEx to see the profile, then pick the chamber. Each chamber with its own profile can be connected by the practice.",
  "social.guide.google.sync.title": "Wait for the first sync",
  "social.guide.google.sync.body": "Every review loads with its stars, text and your reply. The average and total come straight from Google.",
  "social.status.ok": "Connected",
  "social.status.pending": "Loading posts",
  "social.status.needs_reconnect": "Reconnect needed",
  "social.status.failing": "Sync problem",
  "social.status.not_connected": "Not connected",
  "social.status.soon": "Coming soon",
  "social.overview.posts": "{posts} posts · {pinned} of {max} pinned",
  "social.overview.synced": "synced {when}",
  "social.overview.connect": "Connect {platform} to show your posts on your website.",
  "social.overview.soon": "{platform} connections are being set up. See the guide to get ready.",
  "social.stats.subscribers": "Subscribers",
  "social.stats.followers": "Followers",
  "social.stats.views": "Total views",
  "social.stats.posts": "Posts",
  "social.connect.title": "Connect {platform}",
  "social.connect.prepare": "You can get the account ready now with the steps in the guide, so connecting takes a minute when it opens.",
  "social.connect.button": "Connect {platform}",
  "social.connect.youtube.lead": "Paste the channel's link. Only public videos and numbers are read; nobody needs to sign in.",
  "social.connect.youtube.label": "YouTube channel link",
  "social.connect.youtube.hint": "Like youtube.com/@yourchannel, or the channel id that starts with UC.",
  "social.connect.youtube.placeholder": "https://www.youtube.com/@yourchannel",
  "social.connect.youtube.notFound": "YouTube doesn't know this channel. Copy the link from the channel page (it has @ in it) and try again.",
  "social.notConfigured.doctor": "BrandEx is still setting up {platform} connections. We'll let you know when you can connect.",
  "social.notConfigured.staff": "{platform} isn't configured on this server yet: its app keys must be set before clients can connect.",
  "social.account.title": "Connected account",
  "social.account.syncNow": "Sync now",
  "social.account.disconnect": "Disconnect",
  "social.account.disconnectConfirm": "Remove the account and its posts?",
  "social.account.disconnectYes": "Disconnect",
  "social.account.loading": "Loading the posts for the first time. This takes a few minutes; the page updates by itself.",
  "social.account.synced": "Synced {when} from the {source}.",
  "social.site.title": "On your website",
  "social.site.on": "On your website",
  "social.site.off": "Not on your website",
  "social.site.show": "Show this platform on the website",
  "social.site.showHint": "Pinned posts come first; hidden posts never appear.",
  "social.site.count": "Posts shown",
  "social.site.countN": "{n} posts",
  "social.site.fill": "After pinned posts, show",
  "social.site.fillHint": "Fills the remaining places automatically.",
  "social.site.subscribers": "Show the number of subscribers",
  "social.site.followers": "Show the number of followers",
  "social.site.followersHint": "Read from the platform; turn it off while the number is small.",
  "social.fill.latest": "The latest posts",
  "social.fill.most_viewed": "The most viewed",
  "social.fill.most_reactions": "The most liked",
  "social.pinned.title": "Pinned ({pinned} of {max})",
  "social.pinned.hint": "Shown first on your website, in this order.",
  "social.pinned.empty": "Nothing pinned yet. Pin up to three posts from the list below.",
  "social.pinned.earlier": "Move “{title}” earlier",
  "social.pinned.later": "Move “{title}” later",
  "social.posts.title": "All {platform} posts",
  "social.posts.show": "Show",
  "social.posts.sort": "Sort by",
  "social.posts.search": "Search captions",
  "social.posts.loadingTitle": "Loading posts",
  "social.posts.emptyTitle": "No posts",
  "social.posts.empty": "This {platform} account has no public posts yet.",
  "social.posts.noMatch": "No posts match.",
  "social.posts.more": "Show more",
  "social.show.all": "All",
  "social.show.pinned": "Pinned",
  "social.show.hidden": "Hidden",
  "social.sort.newest": "Newest",
  "social.sort.most_viewed": "Most viewed",
  "social.sort.most_reactions": "Most liked",
  "social.sort.most_reach": "Most reach",
  "social.kind.video": "Video",
  "social.kind.post": "Post",
  "social.kind.photo": "Photo",
  "social.kind.carousel": "Carousel",
  "social.kind.reel": "Reel",
  "social.metric.label": "Numbers",
  "social.metric.views": "Views",
  "social.metric.reach": "Reach",
  "social.metric.reactions": "Likes",
  "social.metric.comments": "Comments",
  "social.metric.shares": "Shares",
  "social.post.untitled": "Untitled post",
  "social.post.open": "Open “{title}” on the platform",
  "social.post.pin": "Pin",
  "social.post.unpin": "Unpin",
  "social.post.pinLabel": "Pin “{title}” to the website",
  "social.post.unpinLabel": "Unpin “{title}”",
  "social.post.pinnedN": "Pinned {n}",
  "social.post.pinsFull": "Three posts are pinned. Unpin one first.",
  "social.post.cantPin": "Hidden or removed posts can't be pinned.",
  "social.post.hide": "Hide",
  "social.post.show": "Show",
  "social.post.hideLabel": "Hide “{title}” from the website",
  "social.post.showLabel": "Show “{title}” on the website again",
  "social.post.hidden": "Hidden",
  "social.post.removed": "Removed from the platform",
  "social.guide.title": "How to connect {platform}",
  "social.guide.howItWorks": "How the {platform} connection works",
  "social.guide.help": "{platform} help",
  "social.guide.staffNote": "Steps marked “Doctor” need the doctor's own account. Send them this guide; you can do the rest.",
  "social.guide.doctorStep": "Doctor",
  "social.guide.state.done": "Done",
  "social.guide.state.todo": "To do",
  "social.guide.state.unknown": "Not checked yet",
  "social.guide.youtube.find.title": "Copy the channel's link",
  "social.guide.youtube.find.body": "On a computer, open the channel and copy the address (youtube.com/@…). In the app, open the channel, tap ⋮ or Share, then Copy link.",
  "social.guide.youtube.paste.title": "Paste it and connect",
  "social.guide.youtube.paste.body": "We check the channel with YouTube before saving. Only public videos and numbers are read.",
  "social.guide.youtube.sync.title": "Wait for the first sync",
  "social.guide.youtube.sync.body": "Every public video loads with its views, likes and comments, usually within a few minutes. New videos appear on their own after that.",
  "social.guide.youtube.choose.title": "Pin your best videos",
  "social.guide.youtube.choose.body": "Pin up to three, hide any you don't want on the website, and choose how many it shows.",
  "social.guide.facebook.page.title": "Have a Facebook Page",
  "social.guide.facebook.page.body": "Posts come from a Facebook Page, not a personal profile. Create a Page for the practice if there isn't one.",
  "social.guide.facebook.control.title": "Have full control of the Page",
  "social.guide.facebook.control.body": "The person who connects must manage the Page with full control (Page settings → Page access).",
  "social.guide.facebook.connect.title": "Continue with Facebook",
  "social.guide.facebook.connect.body": "Sign in, allow access to the Page and choose it. Nobody ever asks for your password; you can remove BrandEx in Facebook's settings at any time.",
  "social.guide.instagram.professional.title": "Use a professional account",
  "social.guide.instagram.professional.body": "Instagram only shares posts and numbers of professional (Creator or Business) accounts. Switching is free: Settings → Account type and tools.",
  "social.guide.instagram.link.title": "Link it to the Facebook Page",
  "social.guide.instagram.link.body": "Connect the Instagram account to the practice's Facebook Page in Accounts Center, so one connection covers both.",
  "social.guide.instagram.connect.title": "Continue with Facebook and pick the Instagram account",
  "social.guide.instagram.connect.body": "Instagram professional accounts sign in through the Facebook Page they're linked to.",
  "social.guide.tiktok.login.title": "Be able to sign in to the TikTok account",
  "social.guide.tiktok.login.body": "TikTok connects by signing in to the account itself, once a year. Make sure the doctor (or whoever runs it) can sign in.",
  "social.guide.tiktok.connect.title": "Continue with TikTok",
  "social.guide.tiktok.connect.body": "Sign in to the clinic's TikTok and allow access to the profile, its numbers and its videos.",
  "social.problem.title": "Common problems",
  "social.problem.auth.title": "“Reconnect needed”",
  "social.problem.auth.body": "{platform} stopped accepting BrandEx's access: the password changed, access was removed, or it expired. Connect it again; your pins and hidden posts are kept.",
  "social.problem.auth.short": "{platform} stopped accepting our access. Reconnect it; your pins are kept.",
  "social.problem.not_found.title": "The account can't be found",
  "social.problem.not_found.body": "The account was deleted, renamed or is no longer shared with BrandEx. Check it on {platform}, then connect it again.",
  "social.problem.not_found.short": "{platform} can't find this account any more. Check it, then connect again.",
  "social.problem.rate_limited.title": "“{platform} asked us to slow down”",
  "social.problem.rate_limited.body": "Platforms limit how often they can be read. We try again automatically; nothing for you to do.",
  "social.problem.rate_limited.short": "{platform} asked us to slow down. We'll try again automatically.",
  "social.problem.not_configured.title": "“Not set up yet”",
  "social.problem.not_configured.body": "BrandEx's keys for {platform} are missing or were refused. BrandEx staff fix this on the server; your posts stay as they are meanwhile.",
  "social.problem.not_configured.short": "BrandEx's {platform} keys need attention. We're on it; your posts stay as they are.",
  "social.problem.unavailable.title": "{platform} didn't answer",
  "social.problem.unavailable.body": "A network problem or an outage at {platform}. We retry with growing pauses, at least every few hours.",
  "social.problem.unavailable.short": "{platform} didn't answer. We'll keep trying.",
  "articles.title": "Articles",
  "articles.subtitle": "Write health articles for your website. Patients read these before they book. You approve every article before it goes live.",
  "articles.new": "New article",
  "articles.search": "Search articles",
  "articles.filter": "Filter articles",
  "articles.tab.all": "All",
  "articles.tab.draft": "Drafts",
  "articles.tab.in_review": "In review",
  "articles.tab.published": "Published",
  "articles.tab.unpublished": "Unpublished",
  "articles.status.draft": "Draft",
  "articles.status.in_review": "In review",
  "articles.status.published": "Published",
  "articles.status.unpublished": "Unpublished",
  "articles.empty": "No articles yet. Write your first one: it helps patients trust you before they book.",
  "articles.noMatch": "No articles match.",
  "articles.homeHint": "Your home page shows {max} articles: the ones you pin first ({pinned} of {max} pinned), then your latest.",
  "articles.pinned": "On home page",
  "articles.pin": "Pin to home",
  "articles.unpin": "Unpin",
  "articles.pinLabel": "Pin “{title}” to the home page",
  "articles.unpinLabel": "Unpin “{title}” from the home page",
  "articles.pinsFull": "{max} articles are pinned. Unpin one first.",
  "articles.readingTime": "{n} min read",
  "articles.updated": "Updated {when}",
  "articles.back": "All articles",
  "articles.loadFailed": "The article could not be loaded.",
  "articles.field.title": "Title",
  "articles.field.titleHint": "5 to 120 characters.",
  "articles.field.slug": "Web address",
  "articles.field.slugHint": "Letters, numbers and hyphens. Made from the title if left empty.",
  "articles.field.excerpt": "Summary",
  "articles.field.excerptHint": "Shown in lists and search results. At least 20 characters to publish ({n}/300).",
  "articles.field.language": "Language",
  "articles.field.tags": "Tags",
  "articles.field.tagsHint": "Up to 5, separated by commas.",
  "articles.field.cover": "Cover image",
  "articles.field.coverHint": "At least 400 px wide. PNG, JPEG or WebP, up to 5 MB.",
  "articles.cover.upload": "Upload cover",
  "articles.cover.replace": "Replace cover",
  "articles.cover.remove": "Remove cover",
  "articles.seo": "Search appearance",
  "articles.seo.title": "Search title",
  "articles.seo.titleHint": "Up to 70 characters. Uses the title if empty.",
  "articles.seo.description": "Search description",
  "articles.seo.descriptionHint": "Up to 160 characters. Uses the summary if empty.",
  "articles.settings": "Article settings",
  "articles.ready": "Ready to publish?",
  "articles.ready.title": "A clear title",
  "articles.ready.summary": "A summary of at least 50 characters",
  "articles.ready.cover": "A cover image",
  "articles.ready.length": "At least {n} words",
  "articles.ready.tags": "At least one tag",
  "articles.ready.seo": "A search description (or summary)",
  "articles.ready.done": "done",
  "articles.ready.todo": "not yet",
  "articles.ready.words": "{n} words · about {minutes} min read",
  "articles.seo.preview": "Search result preview",
  "articles.untitled": "Untitled article",
  "articles.body": "Article text",
  "articles.bodyPlaceholder": "Start writing…",
  "articles.write": "Write",
  "articles.preview": "Preview",
  "articles.previewEmpty": "Nothing to preview yet.",
  "articles.saveChanges": "Save changes",
  "articles.saveDraft": "Save draft",
  "articles.saving": "Saving…",
  "articles.saved": "Saved {when}",
  "articles.unsaved": "Unsaved changes",
  "articles.saveFailed": "Could not save. Your text is still here: try again.",
  "articles.liveNote": "This article is live. Changes are saved only when you press Save changes, so readers never see half-written text.",
  "articles.drafterNote": "You can write and send for review. Only the doctor publishes. Editing a published article takes it back to draft until the doctor publishes it again.",
  "articles.waitingDoctor": "Waiting for the doctor to review and publish.",
  "articles.action.submit": "Send for review",
  "articles.action.publish": "Publish",
  "articles.action.publishAgain": "Publish again",
  "articles.action.unpublish": "Unpublish",
  "articles.action.requestChanges": "Send back for changes",
  "articles.action.delete": "Delete",
  "articles.action.deleteConfirm": "Delete this article for good?",
  "articles.action.deleteYes": "Yes, delete",
  "articles.action.keep": "Keep it",
  "articles.live": "Published. Readers can see it now.",
  "articles.viewLive": "View on website",
  "articles.error.slugTaken": "Another article already uses this web address.",
  "articles.error.titleFirst": "Add a title of at least 5 characters first.",
  "articles.error.upload": "The image could not be uploaded.",
  "editor.toolbar": "Formatting",
  "editor.undo": "Undo",
  "editor.redo": "Redo",
  "editor.heading2": "Heading",
  "editor.heading3": "Subheading",
  "editor.heading4": "Small heading",
  "editor.paragraph": "Paragraph",
  "editor.bold": "Bold",
  "editor.italic": "Italic",
  "editor.underline": "Underline",
  "editor.strike": "Strikethrough",
  "editor.code": "Code",
  "editor.link": "Link",
  "editor.removeLink": "Remove link",
  "editor.linkAddress": "Link address",
  "editor.linkApply": "Apply",
  "editor.linkInvalid": "Use a full address such as https://example.com",
  "editor.bulletList": "Bulleted list",
  "editor.orderedList": "Numbered list",
  "editor.quote": "Quote",
  "editor.codeBlock": "Code block",
  "editor.rule": "Divider",
  "editor.image": "Insert image",
  "editor.imageUploading": "Uploading image…",
  "editor.imageFailed": "The image could not be uploaded.",
  "editor.imageAltPrompt": "Describe the image",
  "editor.table": "Insert table",
  "editor.addRow": "Add row",
  "editor.addColumn": "Add column",
  "editor.deleteRow": "Delete row",
  "editor.deleteColumn": "Delete column",
  "editor.deleteTable": "Delete table",
  "editor.embed": "Embed video or map",
  "editor.embedSource": "Link or embed code",
  "editor.embedHint": "Paste a YouTube, Facebook, Instagram, TikTok, Vimeo or Google Maps link, or the <iframe> code a site gives you. Scripts are never kept.",
  "editor.embedTitle": "Title (read aloud by screen readers)",
  "editor.embedAspect": "Shape",
  "editor.embedInsert": "Insert",
  "editor.embedCancel": "Cancel",
  "editor.embedInvalid": "This can't be embedded. Use an https:// link or <iframe> code from the site.",
  "editor.aspect.wide": "Wide (16:9)",
  "editor.aspect.standard": "Standard (4:3)",
  "editor.aspect.square": "Square",
  "editor.aspect.tall": "Tall (9:16)",
  "editor.aspect.portrait": "Portrait (3:4)",
  "editor.words": "Words: {n}",
  "editor.content": "Article text",
  "analytics.title": "Website visitors",
  "analytics.subtitle": "How many people visit your website and tap a booking button. Counted on every page of your site.",
  "analytics.range": "Date range",
  "analytics.range.7": "7 days",
  "analytics.range.30": "30 days",
  "analytics.range.90": "90 days",
  "analytics.range.365": "12 months",
  "analytics.range.custom": "Custom",
  "analytics.from": "From",
  "analytics.to": "To",
  "analytics.apply": "Show",
  "analytics.badRange": "Choose a start on or before the end, at most 400 days apart.",
  "analytics.showing": "{from} to {to}, Bangladesh time",
  "analytics.visits": "Visits",
  "analytics.visitsHint": "Pages opened on your site",
  "analytics.visitors": "Visitors",
  "analytics.visitorsHint": "Different people (device and network)",
  "analytics.clicks": "Booking clicks",
  "analytics.clicksHint": "Taps on call, WhatsApp, Calendly or the booking form",
  "analytics.bookers": "Likely bookings",
  "analytics.bookersHint": "Visitors who used a booking button",
  "analytics.bookersRate": "{rate}% of visitors used a booking button",
  "analytics.daily": "Visits per day",
  "analytics.dailyHint": "Hover or tab through the bars for each day's numbers.",
  "analytics.empty": "No visits in this period yet.",
  "analytics.table": "Show as a table",
  "analytics.day": "Day",
  "analytics.max": "Busiest day: {n}",
  "analytics.tip": "{visits} visits, {visitors} visitors, {clicks} booking clicks",
  "analytics.channels": "Booking buttons used",
  "analytics.channel.call": "Call",
  "analytics.channel.whatsapp": "WhatsApp",
  "analytics.channel.calendly": "Calendly",
  "analytics.channel.form": "Booking form",
  "analytics.noClicks": "No booking clicks in this period.",
  "analytics.sources": "Where visitors came from",
  "analytics.direct": "Direct, apps or unknown",
  "analytics.noVisits": "No visits in this period.",
  "analytics.devices": "Devices",
  "analytics.device.mobile": "Mobile",
  "analytics.device.tablet": "Tablet",
  "analytics.device.desktop": "Computer",
  "analytics.pages": "Most visited pages",
  "analytics.approximate": "Booking clicks are an estimate: a patient may tap a button and not call, or book by phone without visiting the site. Search engines and link previews are not counted.",
  "analytics.privacy": "Patients' privacy: no cookies are used and full IP addresses are never stored, only the network part. Events are kept for about 13 months.",
} as const;

export type MessageKey = keyof typeof en;
